// ============================================================================
// Safe Care - Administration des produits (côté administrateur)
// Module : features/assistance_sociale/pages
// Charte : .tab / .formTitle / .bG / .bt_add / .tabItem / .tabElement / InputForm / SearchInput
// Pattern : identique à Garantie.tsx (formulaire dans Tab + tableau en dessous)
// ============================================================================

import { useState, useEffect, useMemo } from 'react';
import { Table, Spin, Button, Form, Space, Tabs, Modal, Input, InputNumber, Select, Switch, Tag } from 'antd';
import type { TabsProps } from 'antd';
import dayjs from 'dayjs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Stack } from '@mui/material';
import { UserOutlined, ContainerFilled, FileTextOutlined } from '@ant-design/icons';
import { ColumnsType } from 'antd/es/table';
import SweetAlert from 'sweetalert2';
import { useNavigate } from 'react-router-dom';
import { useAuthUser } from '../../../Services/Auth/sessionAuth';
import Utilisateur from '../../../Models/Utilisateur';
import InputForm from '../../../Components/InputT';
import SearchModal from '../../../Components/SearchModal';
import ParamsPicklist from '../../../Types/ParamsPicklist';
import {
  useGetAllProduits,
  useCreateProduit,
  useUpdateProduit,
  useDeleteProduit,
  useGetVersionsByProduit,
  useCreateVersion,
  useUpdateVersion,
  usePublierVersion,
  useGetAllSouscriptions,
  useGetEcheancesBySouscription,
  useGetAllDemandesAssistance,
  useValiderDemandeAssistance,
  useRejeterDemandeAssistance,
  useGetJustificatifsByDemande,
  useGetCatalogueProduits,
  useControleComptesOperat,
  useGetParamFrais,
  useSaveParamFrais,
} from '../hooks';
import { ProduitAssistance, VersionProduitAssistance, SouscriptionSafeCare, EcheanceAssistance, DemandeAssistance, JustificatifDemande, CatalogueProduit, ParamFraisSafeCare } from '../types';
import ConfigurationVersion from './ConfigurationVersion';

// Picklists (réutilisation des tables cSAL existantes)
const searchPlancp: ParamsPicklist = { CodeAgence: '', Table: 'PLANCP', Colonne1: 'IDPLAN', Colonne2: 'LIBELL', Colagence: '101', Choix: 22 };
const searchOperat: ParamsPicklist = { CodeAgence: '', Table: 'OPERAT', Colonne1: 'CODOPE', Colonne2: 'LIBELL', Colagence: '101', Choix: 22 };

// Comptes de contrepartie (COMPTESOPERAT) d'un code opération dans une agence
interface CompteOperatItem { NUMCPT: string; NOMCPT?: string; COTEPART?: number; }
type TypeOperationCodope = 'COTISATION' | 'INDEMNISATION';
interface ComptesCodope { codOpe: string; libelle?: string; comptes: CompteOperatItem[]; erreur?: string; }

const ProduitAdmin = () => {
  const auth = useAuthUser();
  const navigate = useNavigate();
  const user: Utilisateur = JSON.parse(JSON.stringify(auth()));

  const { data: produits, isLoading } = useGetAllProduits();
  const createProduit = useCreateProduit();
  const updateProduit = useUpdateProduit();
  const deleteProduit = useDeleteProduit();

  const [form] = Form.useForm();
  const [formVersion] = Form.useForm();
  const [loading, setLoading] = useState(false);

  // Frais globaux Safe Care (PARAMGENERALSAFECARE) : montant + code opération
  // débités à la souscription / réactivation. Communs à tous les produits —
  // édités ici dans le formulaire produit, sauvegardés avec le produit.
  const { data: paramFraisRes, refetch: refetchFrais } = useGetParamFrais();
  const saveParamFrais = useSaveParamFrais();
  const paramFrais: ParamFraisSafeCare | null =
    paramFraisRes?.StatutCode === 200 ? paramFraisRes.Result?.Table1?.[0] ?? null : null;
  const [Data, SetData] = useState<ProduitAssistance[]>([]);
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [selectedProduit, setSelectedProduit] = useState<ProduitAssistance | null>(null);

  // ---------- Catalogue Safe Care (catégories produits + événements types) ----------
  const { data: catalogueRes } = useGetCatalogueProduits();
  const catalogue: CatalogueProduit[] =
    catalogueRes?.StatutCode === 200 ? catalogueRes.Result?.Table1 ?? [] : [];
  const [picklistProdOpen, setPicklistProdOpen] = useState(false);
  const [picklistSearch, setPicklistSearch] = useState('');
  const [activeTab, setActiveTab] = useState('1');
  const [sbmit, setSbmit] = useState('');

  // Etats des boutons d'action (charte cSAL)
  const [ajoutActve, setAjoutActif] = useState(false);
  const [modifActve, setModifActve] = useState(true);
  const [enregActve, setEnregActve] = useState(true);
  const [supActive, setSupActive] = useState(true);
  const [impActive, setImpActive] = useState(true);
  const [actuActive, setActActive] = useState(true);
  const [annulActive, setAnnulActive] = useState(true);
  const [consulActive, setConsulActive] = useState(false);

  // Etats des boutons d'action pour l'onglet VERSION (charte cSAL)
  const [vAjoutActive, setVAjoutActive] = useState(false);
  const [vModifActive, setVModifActive] = useState(true);
  const [vEnregActive, setVEnregActive] = useState(true);
  const [vActuActive, setVActuActive] = useState(true);
  const [vAnnulActive, setVAnnulActive] = useState(true);
  const [vConsulActive, setVConsulActive] = useState(false);
  const [vSbmit, setVSbmit] = useState('');
  const [selectedVersion, setSelectedVersion] = useState<VersionProduitAssistance | null>(null);
  const [vSelectedRowKeys, setVSelectedRowKeys] = useState<React.Key[]>([]);

  const [selectedSouscription, setSelectedSouscription] = useState<SouscriptionSafeCare | null>(null);
  const [selectedSouscriptionKeys, setSelectedSouscriptionKeys] = useState<React.Key[]>([]);
  const [configOpen, setConfigOpen] = useState(false);

  // Prévisualisation comptable avant enregistrement du produit : codes
  // opération + comptes bénéficiaires (COMPTESOPERAT) de l'agence du produit
  const controleComptesProduit = useControleComptesOperat();
  const [preview, setPreview] = useState<{ values: any; codAge: string; comptes: Record<TypeOperationCodope, ComptesCodope> } | null>(null);
  const previewBloquant = !!preview && (['COTISATION', 'INDEMNISATION'] as const).some(t => !!preview.comptes[t].erreur);

  // Injecte les codes opération globaux des frais dans le formulaire produit
  const chargerFrais = () => {
    if (!paramFrais) return;
    form.setFieldsValue({
      FraisSouscriptionCodOpe: paramFrais.FraisSouscriptionCodOpe ?? '',
      lib_FraisSouscriptionCodOpe: paramFrais.lib_FraisSouscriptionCodOpe ?? '',
      FraisReactivationCodOpe: paramFrais.FraisReactivationCodOpe ?? '',
      lib_FraisReactivationCodOpe: paramFrais.lib_FraisReactivationCodOpe ?? '',
    });
  };

  useEffect(() => {
    chargerFrais();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramFraisRes]);

  useEffect(() => {
    if (produits) SetData(produits);
  }, [produits]);

  function initialiazeButton() {
    setAjoutActif(false);
    setModifActve(true);
    setEnregActve(true);
    setSupActive(true);
    setImpActive(true);
    setActActive(true);
    setAnnulActive(true);
    setConsulActive(false);
  }

  const onSelectChange = (newSelectedRowKeys: any, selectedRows: any) => {
    setSelectedRowKeys(newSelectedRowKeys);
    const record = selectedRows?.[0] ?? Data.find((d: any) => d.IdProduit === newSelectedRowKeys?.[0]);
    if (record) {
      form.setFieldsValue(record);
      chargerFrais(); // les frais sont globaux : toujours les valeurs partagées
      setSelectedProduit(record);
    }
    setModifActve(false);
    setSupActive(false);
    setAnnulActive(false);
    setConsulActive(true);
    setSbmit('update');
  };
  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  function SetStart() {
    setSbmit('add');
    setEditMode(false);
    setSelectedProduit(null);
    setAjoutActif(true);
    setEnregActve(false);
    setAnnulActive(false);
    setConsulActive(true);
    setModifActve(true);
    form.resetFields();
    form.setFieldValue('Statut', 'BROUILLON');
    chargerFrais(); // les frais globaux se réaffichent sur un nouveau produit
  }

  function consult() {
    // Charger les données du produit sélectionné dans le formulaire
    if (selectedProduit) {
      form.setFieldsValue(selectedProduit);
    }
    setAnnulActive(false);
    setModifActve(false);
    setEnregActve(true);
  }

  function modif() {
    setSbmit('update');
    setEditMode(true);
    setModifActve(true);
    setAjoutActif(true);
    setEnregActve(false);
    setConsulActive(true);
    setAnnulActive(false);
  }

  function handleAnulle() {
    form.resetFields();
    initialiazeButton();
    setSelectedRowKeys([]);
    setSelectedProduit(null);
    setSbmit('');
  }

  function actualiser() {
    setLoading(true);
    setTimeout(() => {
      setLoading(false);
      if (produits) SetData(produits);
      SweetAlert.fire({ title: 'Liste actualisée', icon: 'success', timer: 1000 });
    }, 500);
  }

  function handleSupprimer() {
    if (!selectedProduit) {
      SweetAlert.fire({ title: 'Attention', text: 'Veuillez sélectionner un produit.', icon: 'warning' });
      return;
    }
    SweetAlert.fire({
      title: 'Suppression',
      text: `Supprimer le produit "${selectedProduit.Nom}" ? Les versions et règles associées seront aussi supprimées.`,
      showCancelButton: true,
      icon: 'warning',
      confirmButtonColor: 'darkred',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Oui, supprimer',
      cancelButtonText: 'Annuler',
    }).then((res) => {
      if (res.isConfirmed) {
        setLoading(true);
        deleteProduit.mutate(selectedProduit.IdProduit!, {
          onSuccess: (resp) => {
            setLoading(false);
            if (resp?.StatutCode === 200) {
              SweetAlert.fire({ title: 'Succès', text: resp.Message, icon: 'success' });
              handleAnulle();
            } else {
              SweetAlert.fire({ title: 'Attention', text: resp?.Message ?? 'Erreur lors de la suppression', icon: 'warning' });
            }
          },
          onError: () => {
            setLoading(false);
            SweetAlert.fire({ title: 'Erreur', text: 'Erreur lors de la suppression.', icon: 'error' });
          },
        });
      }
    });
  }

  const onFinish = async (values: any) => {
    // Produit mono-agence : agence du créateur, conservée en modification
    const codAge = (sbmit === 'update' ? selectedProduit?.CodAge : undefined) || user.codage || '';
    if (!codAge) {
      SweetAlert.fire({ title: 'Agence non renseignée', text: "Votre agence n'est pas renseignée : impossible de rattacher le produit.", icon: 'warning' });
      return;
    }
    // Codes verrouillés (produit avec versions) : on conserve ceux du produit
    const codes = selectedProduit?.CodOpeVerrouille
      ? { CodOpeCotisation: selectedProduit.CodOpeCotisation, lib_CodOpeCotisation: selectedProduit.lib_CodOpeCotisation, CodOpeIndemnisation: selectedProduit.CodOpeIndemnisation, lib_CodOpeIndemnisation: selectedProduit.lib_CodOpeIndemnisation }
      : { CodOpeCotisation: values.CodOpeCotisation, lib_CodOpeCotisation: values.lib_CodOpeCotisation, CodOpeIndemnisation: values.CodOpeIndemnisation, lib_CodOpeIndemnisation: values.lib_CodOpeIndemnisation };

    // Prévisualisation : comptes bénéficiaires des deux codes dans l'agence du produit
    const charger = async (type: TypeOperationCodope, codOpe?: string, libelle?: string): Promise<ComptesCodope> => {
      if (!codOpe) return { codOpe: '', libelle, comptes: [], erreur: 'Code opération non renseigné.' };
      const res = await controleComptesProduit.mutateAsync({ codOpe: String(codOpe), codAge, typeOperation: type });
      return res?.StatutCode === 200
        ? { codOpe: String(codOpe), libelle, comptes: res.Result?.Table1 ?? [] }
        : { codOpe: String(codOpe), libelle, comptes: [], erreur: res?.Message ?? 'Aucun compte paramétré.' };
    };
    setLoading(true);
    try {
      const [cot, ind] = await Promise.all([
        charger('COTISATION', codes.CodOpeCotisation, codes.lib_CodOpeCotisation),
        charger('INDEMNISATION', codes.CodOpeIndemnisation, codes.lib_CodOpeIndemnisation),
      ]);
      setPreview({ values: { ...values, ...codes }, codAge, comptes: { COTISATION: cot, INDEMNISATION: ind } });
    } catch (err: any) {
      SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Contrôle des comptes impossible.', icon: 'error' });
    } finally {
      setLoading(false);
    }
  };

  // Enregistrement effectif, après confirmation de la prévisualisation
  const enregistrerProduit = (values: any, codAge: string) => {
    setPreview(null);
    const produit: ProduitAssistance = {
      ...values,
      IdProduit: sbmit === 'update' ? selectedProduit?.IdProduit : undefined,
      Statut: values.Statut ?? 'BROUILLON',
      CodAge: codAge,
      UtiSai: user.coduti ?? '',
      UtiVal: sbmit === 'update' ? user.coduti : undefined,
    };
    // Codes opération des frais : globaux (tous produits), enregistrés à chaque
    // save produit. Le montant est calculé par la grille du code à la validation.
    const frais: ParamFraisSafeCare = {
      FraisSouscriptionMontant: null,
      FraisSouscriptionCodOpe: values.FraisSouscriptionCodOpe || null,
      FraisReactivationMontant: null,
      FraisReactivationCodOpe: values.FraisReactivationCodOpe || null,
      UtiSai: user.coduti ?? '',
    };
    saveParamFrais.mutate(frais, {
      onSuccess: (res) => {
        if (res?.StatutCode !== 200)
          SweetAlert.fire({ title: 'Attention', text: res?.Message ?? "Les codes opération des frais n'ont pas pu être enregistrés.", icon: 'warning' });
      },
      onError: () => SweetAlert.fire({ title: 'Attention', text: "Les codes opération des frais n'ont pas pu être enregistrés.", icon: 'warning' }),
    });
    setLoading(true);
    const mutation = sbmit === 'update' ? updateProduit : createProduit;
    mutation.mutate(produit, {
      onSuccess: (res) => {
        setLoading(false);
        if (res?.StatutCode === 200) {
          SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
          handleAnulle();
        } else {
          SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
        }
      },
      onError: (err: any) => {
        setLoading(false);
        SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur de connexion', icon: 'error' });
      },
    });
  };

  const onFinishFailed = () => {
    SweetAlert.fire({
      title: 'Veuillez remplir tous les champs obligatoires',
      icon: 'warning',
    });
  };

  const columnsProduit: ColumnsType<ProduitAssistance> = [
    { title: 'Code', dataIndex: 'Code', width: 100 },
    { title: 'Nom', dataIndex: 'Nom', width: 200 },
    { title: 'Catégorie', dataIndex: 'Categorie', width: 120 },
    { title: 'Statut', dataIndex: 'Statut', width: 100 },
  ];

  // ==================== ONGLET VERSIONS ====================
  const VersionTab = () => {
    const { data: versionsRes, isLoading: loadingVersions } = useGetVersionsByProduit(
      selectedProduit?.IdProduit
    );
    const createVersion = useCreateVersion();
    const updateVersion = useUpdateVersion();
    const publierVersion = usePublierVersion();
    const controleComptes = useControleComptesOperat();

    // Agence de rattachement : les versions héritent de l'agence du produit
    const codAgeVersion = selectedProduit?.CodAge || selectedVersion?.CodAge || user.codage || '';
    // IdVersion à configurer automatiquement après un enregistrement réussi
    const [configAutoIdVersion, setConfigAutoIdVersion] = useState<number | null>(null);

    // Comptes bénéficiaires (COMPTESOPERAT) des codes opération du produit,
    // affichés en lecture seule : les versions héritent des codes du produit.
    const [comptesCodope, setComptesCodope] = useState<Record<TypeOperationCodope, ComptesCodope | null>>({ COTISATION: null, INDEMNISATION: null });

    const chargerComptesCodope = async (type: TypeOperationCodope, codOpe?: string, libelle?: string) => {
      if (!codOpe) {
        setComptesCodope(prev => ({ ...prev, [type]: null }));
        return;
      }
      const res = await controleComptes.mutateAsync({ codOpe: String(codOpe), codAge: codAgeVersion, typeOperation: type });
      setComptesCodope(prev => ({
        ...prev,
        [type]: res?.StatutCode === 200
          ? { codOpe: String(codOpe), libelle, comptes: res.Result?.Table1 ?? [] }
          : { codOpe: String(codOpe), libelle, comptes: [], erreur: res?.Message ?? 'Aucun compte paramétré.' },
      }));
    };

    const versions = versionsRes?.StatutCode === 200 ? versionsRes.Result?.Table1 ?? [] : [];

    // Recharge les comptes des codes opération du produit sélectionné
    useEffect(() => {
      chargerComptesCodope('COTISATION', selectedProduit?.CodOpeCotisation, selectedProduit?.lib_CodOpeCotisation).catch(() => undefined);
      chargerComptesCodope('INDEMNISATION', selectedProduit?.CodOpeIndemnisation, selectedProduit?.lib_CodOpeIndemnisation).catch(() => undefined);
    }, [selectedProduit?.IdProduit, selectedProduit?.CodOpeCotisation, selectedProduit?.CodOpeIndemnisation, codAgeVersion]);

    // Durée du contrat déduite automatiquement des dates d'effet / fin d'effet
    const vDateEffet = Form.useWatch('DateEffet', formVersion);
    const vDateFinEffet = Form.useWatch('DateFinEffet', formVersion);
    useEffect(() => {
      const mois = vDateEffet && vDateFinEffet ? dayjs(vDateFinEffet).diff(dayjs(vDateEffet), 'month') : undefined;
      if (mois !== undefined && mois !== formVersion.getFieldValue('DureeContratMois')) {
        formVersion.setFieldValue('DureeContratMois', mois);
      }
    }, [vDateEffet, vDateFinEffet]);

    // Charger automatiquement la dernière version du produit sélectionné dans le formulaire
    // (ou la version qui vient d'être enregistrée, pour ouvrir sa configuration)
    useEffect(() => {
      if (versions.length > 0 && !vAjoutActive) {
        const versionSauvee = configAutoIdVersion ? versions.find(v => v.IdVersion === configAutoIdVersion) : undefined;
        const derniereVersion = versionSauvee ?? versions[0]; // versions triées par NumeroVersion DESC côté procédure
        // Convertir les dates string → dayjs pour le DatePicker
        const versionData = {
          ...derniereVersion,
          DateEffet: derniereVersion.DateEffet ? dayjs(derniereVersion.DateEffet) : undefined,
          DateFinEffet: derniereVersion.DateFinEffet ? dayjs(derniereVersion.DateFinEffet) : undefined,
        };
        formVersion.setFieldsValue(versionData);
        loadPlansFromJson(derniereVersion.PlansComptables);
        setSelectedVersion(derniereVersion);
        setVSelectedRowKeys([derniereVersion.IdVersion!]);
        setVConsulActive(true);
        setVAnnulActive(false);
        setVModifActive(false);
        if (versionSauvee) {
          setConfigAutoIdVersion(null);
          setConfigOpen(true);
        }
      } else if (versions.length === 0) {
        formVersion.resetFields();
        formVersion.setFieldValue('IdProduit', selectedProduit?.IdProduit);
        formVersion.setFieldValue('NumeroVersion', 1);
        setPlansCotisation([]);
        setPlansIndemnisation([]);
        //setPlansRachat([]);
        setSelectedVersion(null);
        setVSelectedRowKeys([]);
      }
    }, [versions, configAutoIdVersion]);

    // Etats locaux pour multi-plans comptables
    interface PlanItem { idPlan: string; libelle: string; }
    const [plansCotisation, setPlansCotisation] = useState<PlanItem[]>([]);
    const [plansIndemnisation, setPlansIndemnisation] = useState<PlanItem[]>([]);
    //const [plansRachat, setPlansRachat] = useState<PlanItem[]>([]);
    const [vModalOpen, setVModalOpen] = useState(false);

    const handleAddPlanCotisation = () => {
      const id = formVersion.getFieldValue('_PlanCotTmp');
      const lib = formVersion.getFieldValue('lib__PlanCotTmp') ?? id;
      if (!id) { SweetAlert.fire({ title: 'Attention', text: 'Sélectionnez un plan.', icon: 'warning' }); return; }
      if (plansCotisation.some(p => p.idPlan === id)) { SweetAlert.fire({ title: 'Attention', text: 'Plan déjà ajouté.', icon: 'warning' }); return; }
      setPlansCotisation([...plansCotisation, { idPlan: id, libelle: lib }]);
      formVersion.setFieldValue('_PlanCotTmp', undefined);
      formVersion.setFieldValue('lib__PlanCotTmp', undefined);
    };

    const handleAddPlanIndemnisation = () => {
      const id = formVersion.getFieldValue('_PlanIndTmp');
      const lib = formVersion.getFieldValue('lib__PlanIndTmp') ?? id;
      if (!id) { SweetAlert.fire({ title: 'Attention', text: 'Sélectionnez un plan.', icon: 'warning' }); return; }
      if (plansIndemnisation.some(p => p.idPlan === id)) { SweetAlert.fire({ title: 'Attention', text: 'Plan déjà ajouté.', icon: 'warning' }); return; }
      setPlansIndemnisation([...plansIndemnisation, { idPlan: id, libelle: lib }]);
      formVersion.setFieldValue('_PlanIndTmp', undefined);
      formVersion.setFieldValue('lib__PlanIndTmp', undefined);
    };

    // const handleAddPlanRachat = () => {
    //   const id = formVersion.getFieldValue('_PlanRachTmp');
    //   const lib = formVersion.getFieldValue('lib__PlanRachTmp') ?? id;
    //   if (!id) { SweetAlert.fire({ title: 'Attention', text: 'Sélectionnez un plan.', icon: 'warning' }); return; }
    //   if (plansRachat.some(p => p.idPlan === id)) { SweetAlert.fire({ title: 'Attention', text: 'Plan déjà ajouté.', icon: 'warning' }); return; }
    //   setPlansRachat([...plansRachat, { idPlan: id, libelle: lib }]);
    //   formVersion.setFieldValue('_PlanRachTmp', undefined);
    //   formVersion.setFieldValue('lib__PlanRachTmp', undefined);
    // };

    const buildPlansJson = (): string => {
      return JSON.stringify({
        COTISATION: plansCotisation.map(p => p.idPlan),
        INDEMNISATION: plansIndemnisation.map(p => p.idPlan),
      //  RACHAT: plansRachat.map(p => p.idPlan),
      });
    };

    // Charger les plans depuis le JSON d'une version (consultation)
    const loadPlansFromJson = (jsonStr?: string) => {
      setPlansCotisation([]);
      setPlansIndemnisation([]);
     // setPlansRachat([]);
      if (!jsonStr) return;
      try {
        const obj = JSON.parse(jsonStr);
        if (obj.COTISATION) setPlansCotisation(obj.COTISATION.map((id: string) => ({ idPlan: id, libelle: id })));
        if (obj.INDEMNISATION) setPlansIndemnisation(obj.INDEMNISATION.map((id: string) => ({ idPlan: id, libelle: id })));
     //   if (obj.RACHAT) setPlansRachat(obj.RACHAT.map((id: string) => ({ idPlan: id, libelle: id })));
      } catch { /* JSON invalide */ }
    };

    // ==================== Barre d'action VERSION ====================
    function vInitializeButton() {
      setVAjoutActive(false);
      setVModifActive(true);
      setVEnregActive(true);
      setVActuActive(true);
      setVAnnulActive(true);
      setVConsulActive(false);
    }

    function vSetStart() {
      setVSbmit('add');
      setSelectedVersion(null);
      setVSelectedRowKeys([]);
      setVAjoutActive(true);
      setVEnregActive(false);
      setVAnnulActive(false);
      setVConsulActive(true);
      setVModifActive(true);
      formVersion.resetFields();
      formVersion.setFieldValue('IdProduit', selectedProduit?.IdProduit);
      formVersion.setFieldValue('NumeroVersion', versions.length + 1);
      setPlansCotisation([]);
      setPlansIndemnisation([]);
     // setPlansRachat([]);
    }

    // Charge les donnees de la derniere version existante dans le formulaire
    // lors de la creation d'une nouvelle version (copie sans IdVersion)
    function vChargerPrecedente() {
      const sorted = [...versions].sort((a, b) => (b.NumeroVersion ?? 0) - (a.NumeroVersion ?? 0));
      const prev = sorted[0];
      if (!prev) {
        SweetAlert.fire({ title: 'Attention', text: 'Aucune version précédente pour ce produit.', icon: 'warning' });
        return;
      }
      const newNumero = formVersion.getFieldValue('NumeroVersion') ?? (versions.length + 1);
      formVersion.setFieldsValue({
        ...prev,
        IdVersion: undefined,
        IdProduit: selectedProduit?.IdProduit,
        NumeroVersion: newNumero,
        DateEffet: prev.DateEffet ? dayjs(prev.DateEffet) : undefined,
        DateFinEffet: prev.DateFinEffet ? dayjs(prev.DateFinEffet) : undefined,
      });
      loadPlansFromJson(prev.PlansComptables);
      SweetAlert.fire({ title: 'Version précédente chargée', text: `Données de la version ${prev.NumeroVersion} copiées.`, icon: 'success', timer: 1500 });
    }

    function vConsult() {
      if (selectedVersion) {
        formVersion.setFieldsValue({
          ...selectedVersion,
          DateEffet: selectedVersion.DateEffet ? dayjs(selectedVersion.DateEffet) : undefined,
          DateFinEffet: selectedVersion.DateFinEffet ? dayjs(selectedVersion.DateFinEffet) : undefined,
        });
        loadPlansFromJson(selectedVersion.PlansComptables);
      }
      setVAnnulActive(false);
      setVModifActive(false);
      setVEnregActive(true);
    }

    function vModif() {
      setVSbmit('update');
      setVModifActive(true);
      setVAjoutActive(true);
      setVEnregActive(false);
      setVConsulActive(true);
      setVAnnulActive(false);
    }

    function vHandleAnulle() {
      formVersion.resetFields();
      vInitializeButton();
      setSelectedVersion(null);
      setVSelectedRowKeys([]);
      setPlansCotisation([]);
      setPlansIndemnisation([]);
     // setPlansRachat([]);
      setVSbmit('');
    }

    function vActualiser() {
      SweetAlert.fire({ title: 'Liste actualisée', icon: 'success', timer: 1000 });
    }

    const vOnFinish = async (values: any) => {
      // Plan cotisation : comptes clients éligibles à la souscription
      if (plansCotisation.length === 0) {
        SweetAlert.fire({ title: 'Attention', text: 'Ajoutez au moins un plan cotisation.', icon: 'warning' });
        return;
      }
      // Codes opération hérités du produit : ils doivent y être définis
      if (!selectedProduit?.CodOpeCotisation || !selectedProduit?.CodOpeIndemnisation) {
        SweetAlert.fire({
          title: 'Codes opération manquants',
          text: "Définissez les codes opération de cotisation et d'indemnisation sur le produit (onglet « Produit Safe care ») avant de créer une version.",
          icon: 'warning',
        });
        return;
      }
      // if (plansRachat.length === 0) {
      //   SweetAlert.fire({ title: 'Attention', text: 'Ajoutez au moins un plan rachat.', icon: 'warning' });
      //   return;
      // }
      const version: VersionProduitAssistance = {
        ...values,
        IdVersion: vSbmit === 'update' ? selectedVersion?.IdVersion : undefined,
        Statut: vSbmit === 'update' ? (selectedVersion?.Statut ?? 'BROUILLON') : 'BROUILLON',
        UtiSai: user.coduti ?? '',
        UtiVal: vSbmit === 'update' ? user.coduti : undefined,
        PlansComptables: buildPlansJson(),
        CodAge: codAgeVersion,
      };
      setLoading(true);
      const mutation = vSbmit === 'update' ? updateVersion : createVersion;
      mutation.mutate(version, {
        onSuccess: (res) => {
          setLoading(false);
          if (res?.StatutCode === 200) {
            const idSauve = vSbmit === 'update'
              ? selectedVersion?.IdVersion
              : res.Result?.Table1?.[0]?.IdVersion ?? res.Result?.Table?.[0]?.IdVersion;
            SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' }).then(() => {
              vHandleAnulle();
              // Au clic sur OK : la fenêtre de configuration de la version s'ouvre automatiquement
              if (idSauve) setConfigAutoIdVersion(idSauve);
              else setConfigOpen(true);
            });
          } else {
            SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
          }
        },
        onError: (err: any) => {
          setLoading(false);
          SweetAlert.fire({ title: 'Erreur', text: err?.message ?? 'Erreur', icon: 'error' });
        },
      });
    };

    const vOnFinishFailed = () => {
      SweetAlert.fire({ title: 'Veuillez remplir tous les champs obligatoires', icon: 'warning' });
    };

    const handlePublier = () => {
      if (!selectedVersion) {
        SweetAlert.fire({ title: 'Attention', text: 'Aucune version à publier.', icon: 'warning' });
        return;
      }
      SweetAlert.fire({
        title: 'Publication',
        text: `Publier la version ${selectedVersion.NumeroVersion} ?`,
        showCancelButton: true,
        color: '#116599',
        confirmButtonColor: '#116599',
        cancelButtonColor: 'darkred',
        confirmButtonText: 'Oui',
        cancelButtonText: 'Non',
        icon: 'question',
      }).then((res) => {
        if (res.isConfirmed) {
          setLoading(true);
          publierVersion.mutate(
            { idVersion: selectedVersion.IdVersion!, utiVal: user.coduti ?? '' },
            {
              onSuccess: (res) => {
                setLoading(false);
                if (res?.StatutCode === 200) {
                  SweetAlert.fire({ title: 'Succès', text: res.Message, icon: 'success' });
                } else {
                  SweetAlert.fire({ title: 'Attention', text: res?.Message ?? 'Erreur', icon: 'warning' });
                }
              },
            }
          );
        }
      });
    };

    const onVSelectChange = (newSelectedRowKeys: any, selectedRows: any) => {
      setVSelectedRowKeys(newSelectedRowKeys);
      const record = selectedRows?.[0] ?? versions.find((d: any) => d.IdVersion === newSelectedRowKeys?.[0]);
      if (record) {
        formVersion.setFieldsValue({
          ...record,
          DateEffet: record.DateEffet ? dayjs(record.DateEffet) : undefined,
          DateFinEffet: record.DateFinEffet ? dayjs(record.DateFinEffet) : undefined,
        });
        loadPlansFromJson(record.PlansComptables);
        setSelectedVersion(record);
        setVModalOpen(false);
      }
      setVConsulActive(true);
      setVModifActive(false);
      setVEnregActive(true);
      setVAnnulActive(false);
      setVSbmit('update');
    };

    const vRowSelection = {
      selectedRowKeys: vSelectedRowKeys,
      onChange: onVSelectChange,
    };

    const columnsVersion: ColumnsType<VersionProduitAssistance> = [
      { title: 'Version', dataIndex: 'NumeroVersion', width: 80 },
      { title: 'Statut', dataIndex: 'Statut', width: 110 },
      { title: 'Date effet', dataIndex: 'DateEffet', width: 130, render: (d: string) => (d ? dayjs(d).format('DD/MM/YYYY') : '') },
      { title: 'Fin effet', dataIndex: 'DateFinEffet', width: 130, render: (d: string) => (d ? dayjs(d).format('DD/MM/YYYY') : '') },
    ];

    return (
      <Box>
        <Form
          component={false}
          disabled={!vAjoutActive && !vConsulActive}
          autoComplete="off"
          labelAlign="left"
          form={formVersion}
          onFinish={vOnFinish}
          onFinishFailed={vOnFinishFailed}
        >
          {/* Barre d'action VERSION (charte cSAL) */}
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button size="large" className="bt_add" disabled={versions.length === 0} onClick={() => setVModalOpen(true)} type="ghost" htmlType="button" block>Versions</Button>
                <Button size="large" className="bt_add" disabled={vConsulActive} onClick={vConsult} type="ghost" htmlType="button" block>Consulter</Button>
                <Button size="large" className="bt_add" disabled={vAjoutActive} onClick={vSetStart} type="ghost" htmlType="button" block>Ajouter</Button>
                <Button size="large" className="bt_add" disabled={vModifActive} onClick={vModif} type="ghost" htmlType="button" block>Modifier</Button>
                <Button size="large" className="bt_add" disabled={vEnregActive} onClick={() => formVersion.submit()} type="ghost" htmlType="button" block>Enregistrer</Button>
                <Button size="large" className="bt_add" disabled={vSbmit !== 'add' || versions.length === 0} onClick={vChargerPrecedente} type="ghost" htmlType="button" block>Précédente</Button>
                <Button size="large" className="bt_add" disabled={!selectedVersion || selectedVersion.Statut !== 'BROUILLON'} onClick={handlePublier} type="ghost" htmlType="button" block>Publier</Button>
                <Button size="large" className="bt_add" disabled={!selectedVersion} onClick={() => setConfigOpen(true)} type="ghost" htmlType="button" block>Configurer</Button>
                <Button size="large" className="bt_add" disabled={vActuActive} onClick={vActualiser} type="ghost" htmlType="button" block>Actualiser</Button>
                <Button size="large" disabled={vAnnulActive} onClick={vHandleAnulle} className="bt_add" type="ghost" htmlType="reset" block>Annuler</Button>
              </Space>
            </Form.Item>
          </div>

          {/* Formulaire Version */}
          <div className="clientP">
            <div className="tabItem">
              <div>
                <Box fontStyle="inherit" className="border border-left" width="85%" sx={{ p: 1 }}>
                  <legend>Version Safe Care</legend>
                  <Form.Item name="IdProduit" hidden><input type="hidden" /></Form.Item>
                  <Form.Item name="IdVersion" hidden><input type="hidden" /></Form.Item>
                  <Form.Item name="NumeroVersion" hidden><input type="hidden" /></Form.Item>

                  {/* Contexte : produit + version courante */}
                  {selectedProduit && (
                    <Box sx={{
                      mb: 2, px: 1.5, py: 1,
                      borderLeft: '4px solid var(--darkColor)',
                      background: 'var(--lightColor002)',
                      borderRadius: 1,
                      fontWeight: 700,
                      color: 'var(--darkblue)',
                    }}>
                      Produit : {selectedProduit.Nom} ({selectedProduit.Code})
                      <span style={{ margin: '0 8px', color: 'var(--colorOpaque040)' }}>|</span>
                      Agence : {codAgeVersion || '—'}
                      {selectedVersion && (
                        <>
                          <span style={{ margin: '0 8px', color: 'var(--colorOpaque040)' }}>|</span>
                          Version N°{selectedVersion.NumeroVersion} —{' '}
                          <span style={{ color: selectedVersion.Statut === 'PUBLIE' ? '#2e7d32' : '#ed6c02' }}>
                            {selectedVersion.Statut}
                          </span>
                        </>
                      )}
                    </Box>
                  )}

                  <Box display="flex" fontStyle="inherit" sx={{ mb: 1, flexWrap: 'wrap' }}>
                    {/* Colonne 1 — Paramètres de la version */}
                    <Box fontStyle="inherit" sx={{ minWidth: 340, pr: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700, color: 'var(--darkblue)', mb: 1.5,
                          textTransform: 'uppercase', letterSpacing: 0.5,
                        }}
                      >
                        Paramètres
                      </Typography>
                      <InputForm width={9} label="Date d'effet" name="DateEffet" type="date" />
                      <InputForm width={9} notRequired label="Date fin d'effet" name="DateFinEffet" type="date" />
                      <Form.Item
                        name="DureeContratMois"
                        label="Durée contrat (mois)"
                        labelCol={{ span: 9, style: { whiteSpace: 'pre-wrap' } }}
                        className="bordure"
                        tooltip="Calculée automatiquement d'après les dates d'effet et de fin d'effet"
                      >
                        <InputNumber min={0} max={120} style={{ width: '100%' }} placeholder="auto" disabled={!!(vDateEffet && vDateFinEffet)} />
                      </Form.Item>
                      <Form.Item
                        name="RenouvellementAuto"
                        label="Renouvellement auto"
                        labelCol={{ span: 9, style: { whiteSpace: 'pre-wrap' } }}
                        className="bordure"
                        valuePropName="checked"
                      >
                        <Switch />
                      </Form.Item>
                    </Box>
                    <hr />
                    {/* Colonne 2 — Plans comptables clients éligibles */}
                    <Box fontStyle="inherit" sx={{ minWidth: 380, pr: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700, color: 'var(--darkblue)', mb: 1,
                          textTransform: 'uppercase', letterSpacing: 0.5,
                        }}
                      >
                        Plans comptables clients
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                        Comptes autorisés à souscrire. Sélectionnez un plan puis « Ajouter ».
                      </Typography>

                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Cotisation {plansCotisation.length > 0 ? `(${plansCotisation.length})` : ''}
                      </Typography>
                      <SearchModal width={12} notRequired Titre="" Parametre={searchPlancp} label="Plan cotisation" name="_PlanCotTmp" Taille1={167} Taille2={320} form={formVersion} />
                      <Button size="small" type="dashed" onClick={handleAddPlanCotisation} block>+ Ajouter le plan</Button>
                      <Box sx={{ mt: 1, mb: 2 }}>
                        {plansCotisation.map((p, i) => (
                          <Tag
                            key={i}
                            closable={!vEnregActive}
                            onClose={() => setPlansCotisation(plansCotisation.filter((_, idx) => idx !== i))}
                            style={{ marginBottom: 4 }}
                          >
                            {p.idPlan} — {p.libelle}
                          </Tag>
                        ))}
                      </Box>

                      <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                        Indemnisation {plansIndemnisation.length > 0 ? `(${plansIndemnisation.length})` : ''}
                      </Typography>
                      <SearchModal width={12} notRequired Titre="" Parametre={searchPlancp} label="Plan indemnisation" name="_PlanIndTmp" Taille1={167} Taille2={320} form={formVersion} />
                      <Button size="small" type="dashed" onClick={handleAddPlanIndemnisation} block>+ Ajouter le plan</Button>
                      <Box sx={{ mt: 1 }}>
                        {plansIndemnisation.map((p, i) => (
                          <Tag
                            key={i}
                            closable={!vEnregActive}
                            onClose={() => setPlansIndemnisation(plansIndemnisation.filter((_, idx) => idx !== i))}
                            style={{ marginBottom: 4 }}
                          >
                            {p.idPlan} — {p.libelle}
                          </Tag>
                        ))}
                      </Box>
                    </Box>
                    <hr />
                    {/* Colonne 3 — Comptes mouvementés (lecture seule, codes opération du produit) */}
                    <Box fontStyle="inherit" sx={{ flex: 1, minWidth: 380 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 700, color: 'var(--darkblue)', mb: 1,
                          textTransform: 'uppercase', letterSpacing: 0.5,
                        }}
                      >
                        Comptes mouvementés{' '}
                        <Typography component="span" variant="caption" color="textSecondary" sx={{ textTransform: 'none' }}>
                          (hérités du produit)
                        </Typography>
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: 1 }}>
                        Comptes crédités / débités via les codes opération du produit, agence {codAgeVersion || '—'}.
                      </Typography>
                      {(['COTISATION', 'INDEMNISATION'] as const).map(type => {
                        const info = comptesCodope[type];
                        return (
                          <Box key={type} sx={{ mb: 2 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                              {type === 'COTISATION' ? 'Cotisation — comptes crédités' : 'Indemnisation — compte débité'}
                              {info?.codOpe ? ` (code ${info.codOpe}${info.libelle ? ` — ${info.libelle}` : ''})` : ''}
                            </Typography>
                            {!info ? (
                              <Typography variant="caption" color="textSecondary" display="block">Aucun code opération défini sur le produit.</Typography>
                            ) : info.erreur ? (
                              <Typography variant="caption" sx={{ color: '#d32f2f' }} display="block">{info.erreur}</Typography>
                            ) : (
                              <Table
                                size="small"
                                pagination={false}
                                rowKey="NUMCPT"
                                dataSource={info.comptes}
                                columns={[
                                  { title: 'Compte', dataIndex: 'NUMCPT' },
                                  { title: 'Intitulé', dataIndex: 'NOMCPT' },
                                  ...(type === 'COTISATION'
                                    ? [{ title: 'Quote-part', dataIndex: 'COTEPART', align: 'right' as const, render: (v: number) => `${Number(v ?? 0)} %` }]
                                    : []),
                                ]}
                              />
                            )}
                          </Box>
                        );
                      })}
                      <Typography variant="caption" color="textSecondary" display="block">
                        Après « Enregistrer », « Configurer » ouvre les règles de la version (cotisation, éligibilité, événements, carence, rupture, documents).
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </div>
            </div>
          </div>
        </Form>

        {/* Modale de sélection d'une version */}
        <Modal
          title="Sélectionner une version"
          open={vModalOpen}
          onCancel={() => setVModalOpen(false)}
          footer={null}
          width={700}
          zIndex={1050}
          className="safeCareModal"
        >
          <Spin spinning={loadingVersions || loading}>
            <Table
              size="small"
              style={{ width: '100%' }}
              rowKey="IdVersion"
              rowSelection={{ ...vRowSelection, type: 'radio' }}
              columns={columnsVersion}
              dataSource={versions}
              scroll={{ y: 300, x: 10 }}
              pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
            />
          </Spin>
        </Modal>
      </Box>
    );
  };

  // ==================== ONGLET FORMULAIRE PRODUIT (Tab1) ====================
  // Ce tab contient le formulaire avec picklists, comme Tab1 dans Garantie.tsx
  const FormTab = () => {
    return (
      <div className="tabItem">
        <div>
          <Box fontStyle="inherit" className="border border-left" width="85%" position="absolute" sx={{ p: 1 }}>
            <legend>Produit Safe Care</legend>
            <Box display="flex" fontStyle="inherit" sx={{ mb: 1 }}>
              <Box fontStyle="inherit">
                <Form.Item name="IdProduit" hidden>
                  <input type="hidden" />
                </Form.Item>
                {/* Picklist catalogue (charte SearchModal) : sélection du produit type -> remplit Code/Nom/Catégorie/Description */}
                <div className="bordure2">
                  <Form.Item
                    label="Code Produit"
                    required
                    colon={false}
                    labelCol={{ span: 9, style: { whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'keep-all' } }}
                  >
                    <Space.Compact block>
                      <Form.Item name="Code" noStyle rules={[{ required: true, message: 'Code Produit obligatoire' }]}>
                        <Input.Search readOnly onSearch={() => { setPicklistSearch(''); setPicklistProdOpen(true); }} style={{ width: 167 }} />
                      </Form.Item>
                      <Form.Item name="Nom" noStyle>
                        <Input disabled style={{ width: 320 }} />
                      </Form.Item>
                    </Space.Compact>
                  </Form.Item>
                </div>
                <InputForm width={9} label="Catégorie" name="Categorie" type="text" disabled />
                {/* Codes opération : définis une seule fois sur le produit, hérités
                    par toutes ses versions ; verrouillés dès la première version */}
                <SearchModal
                  width={12}
                  Titre=""
                  Parametre={searchOperat}
                  label="Code Opé cotisation"
                  name="CodOpeCotisation"
                  Taille1={167}
                  Taille2={320}
                  form={form}
                  defaultCode={selectedProduit?.CodOpeCotisation}
                  defaultLibelle={selectedProduit?.lib_CodOpeCotisation}
                  disabled={!!selectedProduit?.CodOpeVerrouille}
                />
                <SearchModal
                  width={12}
                  Titre=""
                  Parametre={searchOperat}
                  label="Code Opé indemnisation"
                  name="CodOpeIndemnisation"
                  Taille1={167}
                  Taille2={320}
                  form={form}
                  defaultCode={selectedProduit?.CodOpeIndemnisation}
                  defaultLibelle={selectedProduit?.lib_CodOpeIndemnisation}
                  disabled={!!selectedProduit?.CodOpeVerrouille}
                />
                {selectedProduit?.CodOpeVerrouille && (
                  <Typography variant="caption" color="textSecondary" display="block">
                    Codes opération verrouillés : le produit a déjà {selectedProduit.NbVersions} version(s). Créez un nouveau produit pour en changer.
                  </Typography>
                )}
              </Box>
              <hr />
              <Box fontStyle="inherit">
                <InputForm width={9} label="Description" name="Description" type="TextArea" />
                <Form.Item name="Statut" label="Statut" labelCol={{ span: 9 }}>
                  <Select
                    options={[
                      { value: 'BROUILLON', label: 'Brouillon' },
                      { value: 'PUBLIE', label: 'Publié' },
                      { value: 'ARRETE', label: 'Arrêté' },
                    ]}
                  />
                </Form.Item>
              </Box>
              <hr />
              {/* Frais Safe Care (globaux, tous produits) : seul le code opération
                  est défini ; le montant vient de sa grille tarifaire appliquée à
                  la cotisation de la version, débité à la validation. Vide = gratuit. */}
              <Box fontStyle="inherit">
                <SearchModal
                  width={12}
                  Titre=""
                  notRequired
                  Parametre={searchOperat}
                  label="CodeOpé Frais souscription"
                  name="FraisSouscriptionCodOpe"
                  Taille1={167}
                  Taille2={320}
                  form={form}
                  defaultCode={paramFrais?.FraisSouscriptionCodOpe ?? undefined}
                  defaultLibelle={paramFrais?.lib_FraisSouscriptionCodOpe}
                />
                {paramFrais?.FraisSouscriptionCodOpe && (
                  <Typography variant="caption" display="block" sx={{ mb: 1, color: paramFrais.CodTarifSouscription ? 'text.secondary' : '#d32f2f' }}>
                    {paramFrais.CodTarifSouscription
                      ? `Grille tarifaire : ${paramFrais.CodTarifSouscription}`
                      : 'Aucune grille tarifaire rattachée à ce code : les validations de souscription seront bloquées.'}
                  </Typography>
                )}
                <SearchModal
                  width={12}
                  Titre=""
                  notRequired
                  Parametre={searchOperat}
                  label="CodeOpé Frais réactivation"
                  name="FraisReactivationCodOpe"
                  Taille1={167}
                  Taille2={320}
                  form={form}
                  defaultCode={paramFrais?.FraisReactivationCodOpe ?? undefined}
                  defaultLibelle={paramFrais?.lib_FraisReactivationCodOpe}
                />
                {paramFrais?.FraisReactivationCodOpe && (
                  <Typography variant="caption" display="block" sx={{ color: paramFrais.CodTarifReactivation ? 'text.secondary' : '#d32f2f' }}>
                    {paramFrais.CodTarifReactivation
                      ? `Grille tarifaire : ${paramFrais.CodTarifReactivation}`
                      : 'Aucune grille tarifaire rattachée à ce code : les validations de réactivation seront bloquées.'}
                  </Typography>
                )}
              </Box>
            </Box>
          </Box>
        </div>
      </div>
    );
  };

  const DemandesEnCoursTab = () => {
    const { data: souscriptions, isLoading: loadingSouscriptions } = useGetAllSouscriptions('ACTIVE');
    const { data: echeancesRes, isLoading: loadingEcheances } = useGetEcheancesBySouscription(
      selectedSouscription?.IdSouscription
    );

    const dataSouscriptions = souscriptions ?? [];
    const [searchTerm, setSearchTerm] = useState('');
    const filteredData = useMemo(() => {
      if (!searchTerm.trim()) return dataSouscriptions;
      const term = searchTerm.toLowerCase();
      return dataSouscriptions.filter((d) =>
        [
          d.Numcli,
          d.NOMCLI,
          d.PRECLI,
          d.NomProduit,
          d.NumeroVersion,
          d.NumCptDebit,
          d.Statut,
          d.MotifStatut,
        ]
          .map((v) => String(v ?? '').toLowerCase())
          .some((v) => v.includes(term))
      );
    }, [dataSouscriptions, searchTerm]);

    const echeances: EcheanceAssistance[] =
      echeancesRes?.StatutCode === 200 ? echeancesRes.Result?.Table1 ?? [] : [];

    const onSelectSouscription = (newSelectedRowKeys: React.Key[]) => {
      setSelectedSouscriptionKeys(newSelectedRowKeys);
      const record = dataSouscriptions.find((s) => s.IdSouscription === newSelectedRowKeys[0]) || null;
      setSelectedSouscription(record);
    };

    const rowSelection = {
      selectedRowKeys: selectedSouscriptionKeys,
      type: 'radio' as const,
      onChange: onSelectSouscription,
    };

    const columnsSouscription: ColumnsType<SouscriptionSafeCare> = [
      { title: 'Client', dataIndex: 'Numcli', key: 'Numcli' },
      { title: 'Nom', dataIndex: 'NOMCLI', key: 'NOMCLI' },
      { title: 'Prénom', dataIndex: 'PRECLI', key: 'PRECLI' },
      { title: 'Produit', dataIndex: 'NomProduit', key: 'NomProduit' },
      { title: 'Version', dataIndex: 'NumeroVersion', key: 'NumeroVersion' },
      { title: 'Cotisation', dataIndex: 'MontantCotisation', key: 'MontantCotisation', align: 'right' },
      {
        title: 'Date effet',
        dataIndex: 'DateEffet',
        key: 'DateEffet',
        render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY') : '',
      },
      { title: 'Statut', dataIndex: 'Statut', key: 'Statut' },
    ];

    const columnsEcheance: ColumnsType<EcheanceAssistance> = [
      { title: 'N° éch.', dataIndex: 'IdEcheance', key: 'IdEcheance', align: 'center' },
      {
        title: 'Date échéance',
        dataIndex: 'DateEcheance',
        key: 'DateEcheance',
        render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY') : '',
      },
      { title: 'Montant', dataIndex: 'MontantCotisation', key: 'MontantCotisation', align: 'right' },
      { title: 'Payé', dataIndex: 'MontantPaye', key: 'MontantPaye', align: 'right' },
      {
        title: 'Reste',
        key: 'Reste',
        align: 'right',
        render: (_, r) => (r.MontantCotisation - (r.MontantPaye ?? 0)).toFixed(2),
      },
      {
        title: 'Date paie.',
        dataIndex: 'DatePaiement',
        key: 'DatePaiement',
        render: (v: string) => v ? dayjs(v).format('DD/MM/YYYY') : '',
      },
      { title: 'Statut', dataIndex: 'Statut', key: 'Statut' },
    ];

    const handleExportPDF = () => {
      if (!echeances.length || !selectedSouscription) return;
      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const s = selectedSouscription;
      doc.setFontSize(14);
      doc.text(`Tableau d'amortissement - Souscription #${s.IdSouscription}`, 40, 40);

      doc.setFontSize(10);
      const infoLines = [
        `Client : ${s.Numcli ?? ''} - ${s.NOMCLI ?? ''} ${s.PRECLI ?? ''}`.trim(),
        `Produit : ${s.NomProduit ?? ''} - Version ${s.NumeroVersion ?? ''}`,
        `Durée du contrat : ${s.DureeContratMois != null ? s.DureeContratMois + ' mois' : '-'}`,
        `Période de paiement : ${s.LibelleFrequence ?? s.CodeFrequence ?? '-'}`,
        `Date effet : ${s.DateEffet ? dayjs(s.DateEffet).format('DD/MM/YYYY') : '-'}   Date fin : ${s.DateFin ? dayjs(s.DateFin).format('DD/MM/YYYY') : '-'}`,
        `Montant cotisation : ${s.MontantCotisation ?? '-'}`,
      ];
      doc.text(infoLines, 40, 62);

      autoTable(doc, {
        startY: 62 + infoLines.length * 14 + 8,
        head: [['Date échéance', 'Montant', 'Payé', 'Reste', 'Date paie.', 'Statut']],
        body: echeances.map((e) => [
          e.DateEcheance ? dayjs(e.DateEcheance).format('DD/MM/YYYY') : '',
          e.MontantCotisation,
          e.MontantPaye ?? 0,
          (e.MontantCotisation - (e.MontantPaye ?? 0)).toFixed(2),
          e.DatePaiement ? dayjs(e.DatePaiement).format('DD/MM/YYYY') : '',
          e.Statut,
        ]),
        styles: { fontSize: 9 },
        headStyles: { fillColor: [0, 100, 0] },
      });
      doc.save(`amortissement_souscription_${selectedSouscription.IdSouscription}.pdf`);
    };

    return (
      <div className="tabItem">
        <Box fontStyle="inherit" className="border border-left" width="95%" sx={{ p: 1 }}>
          <legend>Souscriptions actives</legend>
          <Input.Search
            placeholder="Rechercher une souscription..."
            allowClear
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ marginBottom: 8, width: 300 }}
          />
          <Spin spinning={loadingSouscriptions} size="large" style={{ color: '#116599' }}>
            <Table
              size="small"
              rowKey="IdSouscription"
              rowSelection={rowSelection}
              columns={columnsSouscription}
              dataSource={filteredData}
              scroll={{ x: 10 }}
              pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
            />
          </Spin>
        </Box>
        {selectedSouscription && (
          <Box fontStyle="inherit" className="border border-left" width="95%" sx={{ p: 1, mt: 2 }}>
            <legend>Tableau d'amortissement — Souscription #{selectedSouscription.IdSouscription}</legend>
            <Box sx={{ mb: 1, textAlign: 'right' }}>
              <Button type="primary" onClick={handleExportPDF} disabled={!echeances.length}>
                Exporter PDF
              </Button>
            </Box>
            <Spin spinning={loadingEcheances} size="large" style={{ color: '#116599' }}>
              <Table
                size="small"
                rowKey="IdEcheance"
                columns={columnsEcheance}
                dataSource={echeances}
                scroll={{ x: 10 }}
                pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
              />
            </Spin>
          </Box>
        )}
      </div>
    );
  };

  

  const items: TabsProps['items'] = [
    {
      key: '1',
      label: 'Produit Safe care',
      children: FormTab(),
    },
    {
      key: '2',
      label: 'Configuration Produit Safe care',
      children: VersionTab(),
    },
    // {
    //   key: '4',
    //   label: 'Demandes d\'indemnisation',
    //   children: DemandesAssistanceTab(),
    // },
  ];

  return (
    <div className="tab safeCarePage">
      <Typography align="center" className="formTitle">
        <b>{window.location.pathname}</b>
      </Typography>
      <Spin spinning={isLoading || loading} size="large" style={{ color: '#116599' }}>
        <Form
          disabled={activeTab !== '4' && !ajoutActve && !consulActive}
          autoComplete="off"
          labelAlign="left"
          form={form}
          onFinish={onFinish}
          onFinishFailed={onFinishFailed}
          style={{ minWidth: 'auto', maxWidth: 'auto' }}
        >
          {activeTab === '1' && (
          <div className="bG">
            <legend>
              <Typography color="#116599">Actions</Typography>
            </legend>
            <Form.Item shouldUpdate className="bouttong">
              <Space direction="vertical" style={{ width: '100%' }}>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={consulActive}
                  onClick={consult}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Consulter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={ajoutActve}
                  onClick={SetStart}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Ajouter
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={modifActve}
                  onClick={modif}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Modifier
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={enregActve}
                  type="ghost"
                  htmlType="submit"
                  block
                >
                  Enregistrer
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={supActive}
                  type="ghost"
                  htmlType="button"
                  block
                  onClick={handleSupprimer}
                >
                  Supprimer
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={impActive}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Imprimer
                </Button>
                <Button
                  size="large"
                  className="bt_add"
                  disabled={actuActive}
                  onClick={actualiser}
                  type="ghost"
                  htmlType="button"
                  block
                >
                  Actualiser
                </Button>
                <Button
                  size="large"
                  disabled={annulActive}
                  onClick={handleAnulle}
                  className="bt_add"
                  type="ghost"
                  htmlType="reset"
                  block
                >
                  Annuler
                </Button>
              </Space>
            </Form.Item>
          </div>
          )}
          <div className="clientP">
            <div>
              <Tabs
                size="small"
                type="card"
                activeKey={activeTab}
                onChange={setActiveTab}
                defaultActiveKey="1"
                items={items}
                animated={{ inkBar: true, tabPane: true }}
              />
            </div>
          </div>
        </Form>
        <br />
        <br />
        {/* Tableau en dessous du formulaire - visible seulement sur l'onglet Produit (hors Form pour permettre le clic radio) */}
        {activeTab === '1' && (
          <Box display="flex" fontStyle="inherit" className="border-left tabElement" sx={{ p: 2, my: 5 }}>
            <Spin spinning={isLoading || loading}>
              <div>
                <Table
                  size="small"
                  style={{ width: '100%' }}
                  rowKey="IdProduit"
                  rowSelection={{ ...rowSelection, type: 'radio' }}
                  columns={columnsProduit}
                  dataSource={Data}
                  scroll={{ y: 250, x: 10 }}
                  pagination={{ defaultPageSize: 5, pageSizeOptions: [5, 10, 20, 50], showSizeChanger: true, position: ['bottomRight'] }}
                />
              </div>
            </Spin>
          </Box>
        )}
      </Spin>
      <ConfigurationVersion
        open={configOpen}
        idVersion={selectedVersion?.IdVersion}
        idProduit={selectedProduit?.IdProduit}
        categorie={selectedProduit?.Categorie}
        statutVersion={selectedVersion?.Statut}
        onBack={() => setConfigOpen(false)}
      />

      {/* Picklist catalogue — produits Safe Care types (charte SearchModal : Dialog MUI + Tabs) */}
      <Dialog
        className="modal"
        onClick={() => setPicklistProdOpen(false)}
        sx={{ '& .MuiDialog-paper': { minWidth: 'sm', maxHeight: 'md' } }}
        maxWidth="md"
        open={picklistProdOpen}
        onClose={() => { setPicklistProdOpen(false); setPicklistSearch(''); }}
      >
        <DialogTitle>Catalogue Safe Care — produits</DialogTitle>
        <DialogContent dividers></DialogContent>
        <Spin spinning={false} tip="En Cour de Traitement ..." style={{ color: '#116599' }}>
          <Stack direction="row" spacing={5}>
            <Form name="picklistProdForm" wrapperCol={{ span: 16 }} style={{ minWidth: 600, marginLeft: 50 }} initialValues={{ remember: true }}>
              <Tabs defaultActiveKey="3" items={[
                {
                  key: '1',
                  label: <span id="Tab"><Typography><UserOutlined />IDENTIFIANT</Typography></span>,
                  children: <InputForm notRequired name="text" type="text" label="IDENTIFIANT" />,
                },
                {
                  key: '2',
                  label: <span id="Tab"><Typography><FileTextOutlined />LIBELLE</Typography></span>,
                  children: <InputForm notRequired name="text" type="text" label="LIBELLE" />,
                },
                {
                  key: '3',
                  label: <span id="Tab"><Typography><ContainerFilled />ALL</Typography></span>,
                  children: (
                    <Box fontStyle="inherit" display="flex">
                      <InputForm
                        noupercase
                        taille={600}
                        autoFocus
                        notRequired
                        name="text"
                        type="text"
                        label="TEXT"
                        onChange={(e: any) => setPicklistSearch(e.target.value.trim())}
                      />
                      <hr />
                      <Button style={{ top: '10px' }}>Rechercher</Button>
                    </Box>
                  ),
                },
              ]} />
            </Form>
          </Stack>
          <DialogContent dividers>
            <Table
              style={{ width: 'auto' }}
              rowSelection={{
                type: 'radio',
                onChange: (_keys: React.Key[], rows: any[]) => {
                  const record = rows[0] as CatalogueProduit;
                  if (!record) return;
                  form.setFieldsValue({
                    Code: record.CodeProduit,
                    Nom: record.NomProduit,
                    Categorie: record.Categorie,
                    Description: record.DescProduit,
                  });
                  setPicklistProdOpen(false);
                  setPicklistSearch('');
                },
              }}
              scroll={{ y: 250, x: 10 }}
              rowKey="CodeProduit"
              columns={[
                { title: 'Code', dataIndex: 'CodeProduit', width: 130 },
                {
                  title: 'Libelle',
                  dataIndex: 'NomProduit',
                  filteredValue: picklistSearch ? [picklistSearch] : [],
                  onFilter: (value: any, record: any) =>
                    `${record.CodeProduit} ${record.NomProduit} ${record.Categorie}`.toLowerCase().includes(String(value).toLowerCase()),
                },
                { title: 'Catégorie', dataIndex: 'Categorie', width: 140 },
                { title: 'Événements', dataIndex: 'NbEvenements', width: 100 },
              ]}
              dataSource={catalogue}
            />
          </DialogContent>
        </Spin>
        <DialogActions>
          <Button type="default" onClick={() => { setPicklistSearch(''); setPicklistProdOpen(false); }}>Fermer</Button>
        </DialogActions>
      </Dialog>

      {/* Prévisualisation comptable avant enregistrement du produit */}
      <Modal
        title="Prévisualisation comptable du produit Safe Care"
        open={!!preview}
        onCancel={() => setPreview(null)}
        width={760}
        zIndex={1050}
        className="safeCareModal"
        footer={[
          <Button key="annuler" onClick={() => setPreview(null)}>Annuler</Button>,
          <Button
            key="confirmer"
            type="primary"
            disabled={previewBloquant}
            onClick={() => preview && enregistrerProduit(preview.values, preview.codAge)}
          >
            Confirmer et enregistrer
          </Button>,
        ]}
      >
        {preview && (
          <Box>
            <Box sx={{ mb: 2, p: 1.5, borderRadius: 2, background: 'var(--lightColor001)', border: '1px solid var(--colorOpaque030)' }}>
              <Typography sx={{ fontWeight: 600, color: 'var(--darkblue)', fontSize: 14 }}>
                {preview.values.Code} — {preview.values.Nom}
              </Typography>
              <Typography variant="caption" color="textSecondary" display="block">
                Agence du produit : {preview.codAge}
                {preview.values.Categorie ? ` — Catégorie : ${preview.values.Categorie}` : ''}
              </Typography>
            </Box>
            {(['COTISATION', 'INDEMNISATION'] as const).map(type => {
              const info = preview.comptes[type];
              const total = info.comptes.reduce((s, c) => s + Number(c.COTEPART ?? 0), 0);
              return (
                <Box key={type} sx={{ mb: 2 }}>
                  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: 0.25, borderRadius: 1.5, background: 'var(--lightColor002)', mb: 1 }}>
                    <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--darkColor)' }}>
                      {type === 'COTISATION' ? 'Cotisation — comptes bénéficiaires crédités' : 'Indemnisation — compte débité'}
                      {info.codOpe ? ` (code ${info.codOpe}${info.libelle ? ` — ${info.libelle}` : ''})` : ''}
                    </span>
                  </Box>
                  {info.erreur ? (
                    <Typography variant="caption" sx={{ color: '#d32f2f' }} display="block">{info.erreur}</Typography>
                  ) : (
                    <Table
                      size="small"
                      pagination={false}
                      rowKey="NUMCPT"
                      dataSource={info.comptes}
                      columns={[
                        { title: 'Compte', dataIndex: 'NUMCPT' },
                        { title: 'Intitulé', dataIndex: 'NOMCPT' },
                        ...(type === 'COTISATION'
                          ? [{ title: 'Quote-part', dataIndex: 'COTEPART', align: 'right' as const, render: (v: number) => `${Number(v ?? 0)} %` }]
                          : []),
                      ]}
                      summary={type === 'COTISATION' ? () => (
                        <Table.Summary.Row>
                          <Table.Summary.Cell index={0} colSpan={2}><b>Total</b></Table.Summary.Cell>
                          <Table.Summary.Cell index={2} align="right"><b>{total} %</b></Table.Summary.Cell>
                        </Table.Summary.Row>
                      ) : undefined}
                    />
                  )}
                </Box>
              );
            })}
            <Typography variant="caption" color={previewBloquant ? 'error' : 'textSecondary'} display="block">
              {previewBloquant
                ? 'Enregistrement impossible : corrigez le paramétrage des comptes du code opération (COMPTESOPERAT) ou choisissez un autre code.'
                : selectedProduit?.CodOpeVerrouille
                  ? 'Codes opération verrouillés (le produit a déjà des versions) : ils sont repris tels quels.'
                  : 'Ces codes opération seront hérités par toutes les versions du produit et deviendront définitifs dès la création de la première version.'}
            </Typography>
          </Box>
        )}
      </Modal>

      </div>
  );
};

export default ProduitAdmin;